// src/pages/ambulance/AmbulanceSubscription.jsx
import React, { useState, useEffect } from 'react';
import { 
  Check, Crown,  AlertCircle, Calendar, 
  CreditCard, Info, Car, Users,
} from 'lucide-react';
import apiService from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const AmbulanceSubscription = () => {
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [profile, setProfile] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [transactionCode, setTransactionCode] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Subscription plans configuration
  const plans = {
    free: {
      name: 'Free',
      description: 'Basic features to get started',
      price: 0,
      features: {
        maxDrivers: 1,
        maxVehicles: 1,
        requestNotifications: true,
        basicSupport: true,
        analytics: false,
        priorityListing: false,
        autoDispatch: false
      }
    },
    basic: {
      name: 'Basic',
      description: 'Perfect for small ambulance services',
      price: 2500,
      features: {
        maxDrivers: 3,
        maxVehicles: 3,
        requestNotifications: true,
        basicSupport: true,
        analytics: true,
        priorityListing: false,
        autoDispatch: false
      }
    },
    premium: {
      name: 'Premium',
      description: 'Advanced features for growing services',
      price: 5000,
      features: {
        maxDrivers: 10,
        maxVehicles: 10,
        requestNotifications: true,
        basicSupport: true,
        analytics: true,
        priorityListing: true,
        autoDispatch: true,
        prioritySupport: true,
        customBranding: true
      }
    },
    enterprise: {
      name: 'Enterprise',
      description: 'Complete solution for large operations',
      price: 10000,
      features: {
        maxDrivers: 999,
        maxVehicles: 999,
        requestNotifications: true,
        basicSupport: true,
        analytics: true,
        priorityListing: true,
        autoDispatch: true,
        prioritySupport: true,
        customBranding: true,
        dedicatedAccount: true,
        customIntegration: true
      }
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [profileRes, subscriptionRes] = await Promise.all([
        apiService.ambulances.getProfile(),
        apiService.ambulances.getSubscription()
      ]);

      if (profileRes.success) {
        setProfile(profileRes.data);
      }

      if (subscriptionRes.success) {
        setSubscription(subscriptionRes.data);
      }
    } catch (err) {
      console.error('Load data error:', err);
      setError(err.message || 'Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = (planKey) => {
    if (planKey === 'free') return;
    setSelectedPlan(planKey);
    setShowPaymentModal(true);
    setError('');
    setPhoneNumber('');
    setTransactionCode('');
  };

  const handleSubmitPayment = async () => {
    if (!phoneNumber.trim()) {
      setError('Please enter your M-Pesa phone number');
      return;
    }

    if (!transactionCode.trim()) {
      setError('Please enter your M-Pesa transaction code');
      return;
    }

    const phoneRegex = /^(254|0)[17]\d{8}$/;
    if (!phoneRegex.test(phoneNumber.replace(/\s+/g, ''))) {
      setError('Please enter a valid Kenyan phone number');
      return;
    }

    try {
      setSubscribing(true);
      setError('');

      const response = await apiService.ambulances.subscribe({
        plan: selectedPlan,
        paymentMethod: 'mpesa',
        transactionId: transactionCode.trim(),
        mpesaPhone: phoneNumber.trim(),
        amount: plans[selectedPlan].price
      });

      if (response.success) {
        setSuccess('Payment submitted successfully! Your subscription will be activated once verified.');
        setShowPaymentModal(false);
        setTransactionCode('');
        setPhoneNumber('');
        await loadData();
      } else {
        setError(response.message || 'Failed to process subscription');
      }
    } catch (err) {
      console.error('Subscription error:', err);
      setError(err.message || 'Failed to process subscription');
    } finally {
      setSubscribing(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription?')) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await apiService.ambulances.cancelSubscription();

      if (response.success) {
        setSuccess('Subscription cancelled successfully');
        await loadData();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.message || 'Failed to cancel subscription');
      }
    } catch (err) {
      console.error('Cancel subscription error:', err);
      setError(err.message || 'Failed to cancel subscription');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" text="Loading subscription data..." />
      </div>
    );
  }

  const currentPlan = subscription?.plan || 'free';
  const currentPlanData = plans[currentPlan];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                <Crown className="w-8 h-8 text-primary-600" />
                Subscription & Billing
              </h1>
              <p className="text-gray-600 mt-1">Manage your ambulance service subscription</p>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-800 font-medium">{error}</p>
            </div>
            <button onClick={() => setError('')} className="text-red-600 hover:text-red-800 text-xl font-bold">×</button>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 flex items-start gap-3">
            <Check className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-green-800 font-medium">{success}</p>
            </div>
            <button onClick={() => setSuccess('')} className="text-green-600 hover:text-green-800 text-xl font-bold">×</button>
          </div>
        )}

        {/* Current Subscription */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Crown className="w-6 h-6" />
                <h2 className="text-2xl font-bold">{currentPlanData?.name} Plan</h2>
              </div>
              <p className="text-red-100 mb-4">{currentPlanData?.description}</p>

              {subscription && (
                <div className="mt-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${
                      subscription.status === 'active' ? 'bg-green-400' :
                      subscription.status === 'pending' ? 'bg-yellow-400' : 'bg-gray-400'
                    }`}></div>
                    <p className="text-sm">
                      Status: <span className="font-semibold capitalize">{subscription.status}</span>
                      {subscription.status === 'pending' && ' - Awaiting verification'}
                    </p>
                  </div>

                  {subscription.endDate && subscription.status === 'active' && (
                    <p className="text-sm flex items-center gap-2">
                      <Calendar size={16} />
                      Expires: {new Date(subscription.endDate).toLocaleDateString()}
                    </p>
                  )}

                  {/* Usage Stats */}
                  <div className="mt-4 grid grid-cols-2 gap-4">
                    <div className="bg-red-600 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Users className="w-4 h-4" />
                        <span className="text-xs">Drivers</span>
                      </div>
                      <p className="text-lg font-bold">
                        {profile?.limits?.currentDrivers || 0} / {profile?.limits?.maxDrivers || 1}
                      </p>
                    </div>
                    <div className="bg-red-600 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Car className="w-4 h-4" />
                        <span className="text-xs">Vehicles</span>
                      </div>
                      <p className="text-lg font-bold">
                        {profile?.limits?.currentVehicles || 0} / {profile?.limits?.maxVehicles || 1}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="text-right">
              <p className="text-4xl font-bold">
                KES {currentPlanData?.price?.toLocaleString() || 0}
              </p>
              <p className="text-red-100 text-sm mb-4">per month</p>

              {subscription && currentPlan !== 'free' && subscription.status === 'active' && (
                <button
                  onClick={handleCancelSubscription}
                  className="px-4 py-2 border-2 border-white text-white rounded-lg hover:bg-white hover:text-red-600 transition font-semibold"
                >
                  Cancel Subscription
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Usage Warning */}
        {profile && (
          <>
            {profile.limits?.currentDrivers >= profile.limits?.maxDrivers && (
              <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-4 flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-orange-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-orange-900">Driver Limit Reached</p>
                  <p className="text-sm text-orange-700 mt-1">
                    You've reached your driver limit. Upgrade to add more drivers.
                  </p>
                </div>
              </div>
            )}

            {profile.limits?.currentVehicles >= profile.limits?.maxVehicles && (
              <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-4 flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-orange-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-orange-900">Vehicle Limit Reached</p>
                  <p className="text-sm text-orange-700 mt-1">
                    You've reached your vehicle limit. Upgrade to add more vehicles.
                  </p>
                </div>
              </div>
            )}
          </>
        )}

        {/* Available Plans */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Available Plans</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Object.entries(plans).map(([key, plan]) => {
              const isCurrent = key === currentPlan;
              const isPremium = key === 'premium';

              return (
                <div
                  key={key}
                  className={`relative bg-white rounded-xl border-2 p-6 transition-all ${
                    isPremium
                      ? 'border-yellow-400 shadow-lg transform scale-105'
                      : isCurrent
                      ? 'border-red-500 shadow-md'
                      : 'border-gray-200 hover:border-red-300 hover:shadow-md'
                  }`}
                >
                  {isPremium && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                        <Crown size={12} />
                        Popular
                      </span>
                    </div>
                  )}

                  {isCurrent && (
                    <div className="absolute -top-4 right-4">
                      <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                        Current
                      </span>
                    </div>
                  )}

                  <div className="text-center mb-6 mt-4">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <p className="text-gray-600 text-xs mb-4">{plan.description}</p>
                    <div className="mb-4">
                      <span className="text-3xl font-bold text-gray-900">
                        {plan.price === 0 ? 'Free' : `KES ${plan.price.toLocaleString()}`}
                      </span>
                      {plan.price > 0 && <span className="text-gray-600 text-sm ml-1">/mo</span>}
                    </div>
                  </div>

                  <ul className="space-y-2 mb-6">
                    <li className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>{plan.features.maxDrivers === 999 ? 'Unlimited' : plan.features.maxDrivers} drivers</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>{plan.features.maxVehicles === 999 ? 'Unlimited' : plan.features.maxVehicles} vehicles</span>
                    </li>
                    {plan.features.analytics && (
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-500" />
                        <span>Advanced analytics</span>
                      </li>
                    )}
                    {plan.features.priorityListing && (
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-500" />
                        <span>Priority listing</span>
                      </li>
                    )}
                    {plan.features.autoDispatch && (
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-500" />
                        <span>Auto-dispatch</span>
                      </li>
                    )}
                    {plan.features.prioritySupport && (
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-500" />
                        <span>Priority support</span>
                      </li>
                    )}
                    {plan.features.dedicatedAccount && (
                      <li className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-500" />
                        <span>Dedicated account</span>
                      </li>
                    )}
                  </ul>

                  <button
                    className={`w-full py-2 rounded-lg font-semibold transition ${
                      isCurrent
                        ? 'bg-gray-100 text-gray-600 cursor-not-allowed'
                        : key === 'free'
                        ? 'bg-gray-100 text-gray-600 cursor-not-allowed'
                        : isPremium
                        ? 'bg-yellow-400 text-yellow-900 hover:bg-yellow-500'
                        : 'bg-red-600 text-white hover:bg-red-700'
                    }`}
                    disabled={isCurrent || key === 'free'}
                    onClick={() => handleSelectPlan(key)}
                  >
                    {isCurrent ? 'Current Plan' : key === 'free' ? 'Free Plan' : 'Upgrade Now'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Payment Modal */}
        {showPaymentModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Subscribe to {plans[selectedPlan]?.name}
              </h3>

              {/* Plan Summary */}
              <div className="bg-gray-50 p-4 rounded-lg border mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Plan:</span>
                  <span className="font-semibold">{plans[selectedPlan]?.name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Amount:</span>
                  <span className="font-bold text-xl text-gray-900">
                    KES {plans[selectedPlan]?.price.toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-2">M-Pesa Instructions</h4>
                    <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                      <li>Go to M-Pesa menu</li>
                      <li>Select Lipa na M-Pesa → Pay Bill</li>
                      <li>Business Number: <strong>522522</strong></li>
                      <li>Account: <strong>MEDIGO{plans[selectedPlan]?.price}</strong></li>
                      <li>Amount: <strong>KES {plans[selectedPlan]?.price}</strong></li>
                      <li>Enter PIN and confirm</li>
                      <li>Enter details below</li>
                    </ol>
                  </div>
                </div>
              </div>

              {/* Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    M-Pesa Phone Number *
                  </label>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="0712345678"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    disabled={subscribing}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Transaction Code *
                  </label>
                  <input
                    type="text"
                    value={transactionCode}
                    onChange={(e) => setTransactionCode(e.target.value.toUpperCase())}
                    placeholder="QGH7D8F9KL"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 font-mono"
                    disabled={subscribing}
                  />
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowPaymentModal(false);
                    setError('');
                  }}
                  disabled={subscribing}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitPayment}
                  disabled={subscribing || !phoneNumber.trim() || !transactionCode.trim()}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:bg-gray-400 flex items-center justify-center gap-2"
                >
                  {subscribing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4" />
                      Submit Payment
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AmbulanceSubscription;